'use client';

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { Product } from '@/lib/shopify/types';

interface AccordionItem {
  id: string;
  title: string;
  content: React.ReactNode;
}

interface ProductAccordionProps {
  descriptionHtml: string;
  product: Product;
}

const SECTION_PATTERN = /(<h[1-6]\b[^>]*>[\s\S]*?<\/h[1-6]>)([\s\S]*?)(?=<h[1-6]\b|$)/gi;
const PARAGRAPH_PATTERN = /<p\b[^>]*>([\s\S]*?)<\/p>/gi;

function getPlainText(html: string) {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatLeadingParagraphsAsList(
  body: string,
  className: string,
  ordered: boolean,
  shouldInclude: (text: string) => boolean
) {
  const leadingParagraphs = body.match(/^\s*(?:<p\b[^>]*>[\s\S]*?<\/p>\s*)+/i)?.[0];
  if (!leadingParagraphs) return body;

  const paragraphs = Array.from(leadingParagraphs.matchAll(PARAGRAPH_PATTERN));
  const listItems: string[] = [];

  for (const paragraph of paragraphs) {
    if (!shouldInclude(getPlainText(paragraph[1]))) break;
    listItems.push(paragraph[1]);
  }

  if (listItems.length < 2) return body;

  const consumedParagraphs = paragraphs
    .slice(0, listItems.length)
    .map((paragraph) => paragraph[0])
    .join('');
  const listTag = ordered ? 'ol' : 'ul';
  const list = `<${listTag} class="${className}">${listItems.map((item) => `<li>${item}</li>`).join('')}</${listTag}>`;

  return body.replace(consumedParagraphs, list);
}

function formatLeadingList(body: string, ordered: boolean, className: string) {
  const listMatch = body.match(/^\s*<(ul|ol)\b([^>]*)>([\s\S]*?)<\/\1>/i);
  if (!listMatch) return body;

  const listTag = ordered ? 'ol' : 'ul';
  const existingClass = listMatch[2].match(/\bclass=(['"])(.*?)\1/i);
  const attributes = existingClass
    ? listMatch[2].replace(existingClass[0], `class="${Array.from(new Set(`${existingClass[2]} ${className}`.split(/\s+/))).join(' ')}"`)
    : `${listMatch[2]} class="${className}"`;
  const list = `<${listTag}${attributes}>${listMatch[3]}</${listTag}>`;
  return body.replace(listMatch[0], list);
}

function isCareInstruction(text: string) {
  return /^(?:care:\s*|sponge clean|hand wash|machine wash|wash |use a mild detergent|do not |allow (?:to )?dry|air dry|dry naturally|dry flat|line dry|line drying|reshape |iron |keep away|avoid aggressive)/i.test(text);
}

function formatCombinedSizeAndCare(body: string) {
  const leadingParagraphs = body.match(/^\s*(?:<p\b[^>]*>[\s\S]*?<\/p>\s*)+/i)?.[0];
  if (!leadingParagraphs) return body;

  const paragraphs = Array.from(leadingParagraphs.matchAll(PARAGRAPH_PATTERN));
  const careStart = paragraphs.findIndex((paragraph) => isCareInstruction(getPlainText(paragraph[1])));
  if (careStart < 1) return body;

  let careEnd = careStart;
  while (careEnd < paragraphs.length && isCareInstruction(getPlainText(paragraphs[careEnd][1]))) {
    careEnd += 1;
  }

  const sizeItems = paragraphs.slice(0, careStart).map((paragraph) => `<li>${paragraph[1]}</li>`).join('');
  const careItems = paragraphs.slice(careStart, careEnd).map((paragraph, index) => {
    const content = index === 0 ? paragraph[1].replace(/^\s*Care:\s*/i, '') : paragraph[1];
    return `<li>${content}</li>`;
  }).join('');
  const consumedParagraphs = paragraphs.slice(0, careEnd).map((paragraph) => paragraph[0]).join('');
  const replacement = [
    `<ul class="product-description-list product-description-list--details">${sizeItems}</ul>`,
    '<h4 class="product-description-subheading">Care instructions</h4>',
    `<ul class="product-description-list product-description-list--care">${careItems}</ul>`,
  ].join('');

  return body.replace(consumedParagraphs, replacement);
}

function formatFeatureSection(body: string) {
  const leadingParagraphs = body.match(/^\s*(?:<p\b[^>]*>[\s\S]*?<\/p>\s*)+/i)?.[0];
  if (!leadingParagraphs) return body;

  const paragraphs = Array.from(leadingParagraphs.matchAll(PARAGRAPH_PATTERN));
  const careStart = paragraphs.findIndex((paragraph) => isCareInstruction(getPlainText(paragraph[1])));
  const proseStart = paragraphs.findIndex((paragraph) => getPlainText(paragraph[1]).length > 120);
  const boundaries = [careStart, proseStart].filter((index) => index >= 0);
  const featureEnd = boundaries.length > 0 ? Math.min(...boundaries) : paragraphs.length;
  const features = paragraphs.slice(0, featureEnd);
  if (features.length < 2) return body;

  const featureList = `<ul class="product-description-list product-description-list--features">${features.map((paragraph) => `<li>${paragraph[1]}</li>`).join('')}</ul>`;
  const consumedFeatures = features.map((paragraph) => paragraph[0]).join('');
  let formattedBody = body.replace(consumedFeatures, featureList);

  if (careStart === featureEnd) {
    let careEnd = careStart;
    while (careEnd < paragraphs.length && isCareInstruction(getPlainText(paragraphs[careEnd][1]))) {
      careEnd += 1;
    }
    const careParagraphs = paragraphs.slice(careStart, careEnd);
    const careItems = careParagraphs.map((paragraph) => `<li>${paragraph[1]}</li>`).join('');
    formattedBody = formattedBody.replace(
      careParagraphs.map((paragraph) => paragraph[0]).join(''),
      `<h4 class="product-description-subheading">Care instructions</h4><ul class="product-description-list product-description-list--care">${careItems}</ul>`
    );
  }

  return formattedBody;
}

function formatProductDescription(descriptionHtml: string) {
  return descriptionHtml.replace(SECTION_PATTERN, (section, heading: string, body: string) => {
    const headingText = getPlainText(heading).toLowerCase();

    if (headingText === 'key features') {
      const formattedBody = formatFeatureSection(body);
      return `${heading}${formatLeadingList(
        formattedBody,
        false,
        'product-description-list product-description-list--features'
      )}`;
    }

    if (headingText === 'care instructions' || headingText === 'wash and care instructions') {
      const formattedBody = formatLeadingList(formatLeadingParagraphsAsList(
        body,
        'product-description-list product-description-list--care',
        false,
        isCareInstruction
      ), false, 'product-description-list product-description-list--care');
      return `${heading}${formattedBody}`;
    }

    if (headingText === 'size and care') {
      return `${heading}${formatCombinedSizeAndCare(body)}`;
    }

    return section;
  });
}

const ADULT_TOP_MEASUREMENTS: Record<string, { chest: string; length: string }> = {
  XS: { chest: '86–91', length: '66' },
  S: { chest: '91–96', length: '69' },
  M: { chest: '96–101', length: '72' },
  L: { chest: '101–106', length: '74' },
  XL: { chest: '106–111', length: '76' },
  '2XL': { chest: '111–116', length: '78' },
};

function getSizeGuide(product: Product) {
  const title = product.title.toLowerCase();
  const type = product.productType.toLowerCase();
  // The existing numbers are the only confirmed measurements in the current
  // catalogue and describe the standard round-neck/long-sleeve T-shirt blank.
  // Do not reuse them for polos, sports tops or other garment types: that was
  // the mismatch Cris reported.
  const isAdultTshirt = type === 't-shirt' && /(round neck|long sleeve)/.test(title);
  const sizeOption = product.options.find((option) => option.name.toLowerCase() === 'size');

  if (isAdultTshirt) {
    const sizes = sizeOption?.values || Object.keys(ADULT_TOP_MEASUREMENTS);
    return {
      heading: 'Adult garment measurements',
      intro: 'These are the standard adult T-shirt measurements currently available for this product.',
      columns: ['Size', 'Chest (cm)', 'Length (cm)'],
      rows: sizes.map((size) => [size, ADULT_TOP_MEASUREMENTS[size]?.chest || 'Not supplied', ADULT_TOP_MEASUREMENTS[size]?.length || 'Not supplied']),
      note: sizes.some((size) => !ADULT_TOP_MEASUREMENTS[size])
        ? 'Some sizes do not have confirmed measurements yet. We are waiting for the garment measurements before publishing them.'
        : 'Measurements are approximate and may vary slightly.',
    };
  }

  const sizeLabel = sizeOption?.values.length ? ` Available options: ${sizeOption.values.join(', ')}.` : '';
  return {
    heading: 'Product measurements to be confirmed',
    intro: `This is not an adult T-shirt or polo size chart, so the shared chest-and-length table is not shown here.${sizeLabel}`,
    columns: [],
    rows: [],
    note: 'Please contact us if you need help choosing a size. We are awaiting the confirmed measurements for this product type.',
  };
}

export default function ProductAccordion({ descriptionHtml, product }: ProductAccordionProps) {
  const [openItems, setOpenItems] = useState<string[]>(['description']);
  const sizeGuideRef = useRef<HTMLDivElement>(null);

  const toggleItem = (id: string) => {
    setOpenItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Listen for "open-size-guide" event from Size Guide buttons
  useEffect(() => {
    const handleOpenSizeGuide = () => {
      setOpenItems((prev) =>
        prev.includes('size-guide') ? prev : [...prev, 'size-guide']
      );
      setTimeout(() => {
        sizeGuideRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    };
    window.addEventListener('open-size-guide', handleOpenSizeGuide);
    return () => window.removeEventListener('open-size-guide', handleOpenSizeGuide);
  }, []);

  const items: AccordionItem[] = [
    {
      id: 'description',
      title: 'Description',
      content: (() => {
        const formattedDescription = formatProductDescription(descriptionHtml || '');
        // Some Shopify products store paragraph breaks as bare \n\n between
        // plain-text chunks instead of wrapping them in <p> tags. Those need
        // pre-line whitespace handling. Well-formed HTML with <p> tags does
        // NOT — pre-line would render the literal newlines between </p> and
        // <p> as extra blank lines on top of the <p> margin, doubling the
        // gap. We use <p> presence as the signal: if <p> is used, margins
        // handle spacing; otherwise fall back to pre-line.
        const hasParagraphTags = /<p[\s>]/i.test(formattedDescription);
        return (
          <div
            className={`product-description${hasParagraphTags ? '' : ' product-description-raw'} text-sm text-[var(--color-text-secondary)]`}
            dangerouslySetInnerHTML={{ __html: formattedDescription || '<p>No description available.</p>' }}
          />
        );
      })(),
    },
    {
      id: 'size-guide',
      title: 'Size Guide',
      content: (() => {
        const guide = getSizeGuide(product);
        const isPending = guide.rows.length === 0;
        return (
          <div className="text-sm text-[var(--color-text-secondary)] space-y-4">
            <div className={isPending ? 'rounded-sm border border-[var(--color-border)] bg-[var(--color-off-white)] p-4' : ''}>
              <h4 className="font-medium text-black mb-3">{guide.heading}</h4>
              <p className="leading-6">{guide.intro}</p>
              {isPending && <p className="mt-3 text-xs leading-5">{guide.note}</p>}
            </div>
            {guide.rows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-[var(--color-border)]">
                      {guide.columns.map((column) => <th key={column} className="py-2 pr-4 font-medium text-black">{column}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {guide.rows.map((row) => (
                      <tr key={row[0]} className="border-b border-[var(--color-border)] last:border-0">
                        {row.map((value, index) => <td key={`${row[0]}-${index}`} className="py-2 pr-4">{value}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {!isPending && <p className="text-xs">{guide.note}</p>}
          </div>
        );
      })(),
    },
    {
      id: 'delivery',
      title: 'Delivery & Returns',
      content: (
        <div className="text-sm text-[var(--color-text-secondary)] space-y-4">
          <div>
            <h4 className="font-medium text-black mb-2">Delivery</h4>
            <ul className="space-y-1">
              <li>Standard UK Delivery: 3-5 working days - £6.99</li>
              <li>Next Day UK Delivery: 1 working day - £9.99</li>
              <li>Free Local Collection: usually ready in 24 hours from Birmingham (126 Beeches Rd, B42 2HQ)</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-black mb-2">Returns</h4>
            <p>
              We accept returns within 30 days of delivery for unworn items in original packaging.
              Custom printed or embroidered items cannot be returned unless faulty.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-black mb-2">Need Help?</h4>
            <p>
              Contact us at{' '}
              <a href="mailto:hello@oktshirt.co.uk" className="underline hover:text-black">
                hello@oktshirt.co.uk
              </a>{' '}
              or via WhatsApp for assistance.
            </p>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="border-t border-[var(--color-border)]">
      {items.map((item) => (
        <div key={item.id} ref={item.id === 'size-guide' ? sizeGuideRef : undefined} className="border-b border-[var(--color-border)]">
          <button
            onClick={() => toggleItem(item.id)}
            className="w-full flex items-center justify-between py-4 text-left"
          >
            <span className="text-sm font-medium uppercase tracking-wider">{item.title}</span>
            <motion.svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-5 h-5"
              animate={{ rotate: openItems.includes(item.id) ? 180 : 0 }}
              transition={{ duration: 0.2 }}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </motion.svg>
          </button>
          <AnimatePresence>
            {openItems.includes(item.id) && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="pb-4">{item.content}</div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
}
